import PropTypes from 'prop-types'
import React from 'react'
import { findDOMNode } from 'react-dom'
import { injectIntl, FormattedMessage } from 'react-intl'
import { SubmissionError } from 'redux-form'
import {
  CARD_ELEMENT_NAME_MAX_LENGTH,
  NOTE_CONTENT_MAX_LENGTH
} from 'const/limits'
import {
  ToggleModal
} from 'components/Modal'
import ElementContentIcon from '../../ContentIcon'
import {
  Content,
  ContentBody,
  ContentHeader,
  ContentTitle
} from 'components/Content'
import createEmptyRichText from 'components/RichText/utils/createEmptyRichText'
import getCharCount from 'components/RichText/utils/getCharCount'
import trimRichText from 'components/RichText/utils/trim'
import KanBoIcon from 'components/KanBoIcon'
import AddNoteForm from './AddNoteForm'
import AddChecklistForm from './AddChecklistForm'
import AddDocumentListForm from './AddDocumentListForm'
import AddDocumentFolderForm from './AddDocumentFolderForm'
import AddSubCardGroupForm from './AddSubCardGroupForm'
import AddOfficeDocumentForm from '../../AddOfficeDocumentForm'
import classes from './AddElement.module.scss'
import { ModalWithBoardContext } from 'components/Modal'
import AddElementButton from '../../AddElementButton'
import DocumentTemplates, { DocumentTemplateItem } from 'routes/Board/DocumentTemplates'
import {
  LeftSidebarView,
  ViewBody,
  BoardModalView
} from 'components/Sidebar'
import withLeftSidebar from 'components/Sidebar/withLeftSidebar'
import {
  FormGroup,
  ControlLabel,
} from 'react-bootstrap'
import Documents from 'routes/Board/DocumentList/Documents'
import DocumentItem from 'routes/Board/DocumentList/DocumentItem'
import Button from 'components/Button'
import styled from 'styled-components'
import {
  MenuItem as ModalMenuItem
} from 'components/Menu'

const SelectButton = styled(Button)`
  float: right;
`

const ELEMENT = {
  NOTE: 'note',
  CHECKLIST: 'checklist',
  DOCUMENT_LIST: 'document_list',
  DOCUMENT_FOLDER: 'document_folder',
  DOCUMENT_FOLDER_CREATOR: 'document_folder_creator',
  SUBCARD_GROUP: 'subcard_group',
  DOCUMENT: 'document',
}

const CARD_ELEMENTS = [{
  id: ELEMENT.NOTE,
  iconName: 'kanbo-24-note',
  formattedMessageId: 'NOTE_HEADER',
}, {
  id: ELEMENT.CHECKLIST,
  iconName: 'kanbo-24-task-list',
  formattedMessageId: 'CHECKLIST_HEADER',
}, {
  id: ELEMENT.SUBCARD_GROUP,
  iconName: 'kanbo-24-relation-child',
  formattedMessageId: 'SUBCARD_GROUP',
}, {
  id: ELEMENT.DOCUMENT_LIST,
  iconName: 'kanbo-24-document-group',
  formattedMessageId: 'DOCUMENT_LIST',
}, {
  id: ELEMENT.DOCUMENT_FOLDER,
  iconName: 'kanbo-24-document-folder',
  formattedMessageId: 'DOCUMENT_FOLDER',
}, {
  id: ELEMENT.DOCUMENT,
  iconName: 'kanbo-24-single-document',
  formattedMessageId: 'DOCUMENT',
}]

const CARD_TEMPLATE_ELEMENTS = [{
  id: ELEMENT.DOCUMENT_FOLDER_CREATOR,
  iconName: 'kanbo-24-document-folder-new',
  formattedMessageId: 'DOCUMENT_FOLDER_CREATOR',
}]

const DOCUMENT = {
  EXISTING: 'existing',
  FROM_TEMPLATE: 'from_template',
  WORD: 'word',
  EXCEL: 'excel',
  POWERPOINT: 'powerpoint',
}

const DOCUMENTS = [{
  id: DOCUMENT.EXISTING,
  iconName: 'kanbo-24-single-document',
  formattedMessageId: 'CARD_ADD_EXISTING_DOCUMENT',
}, {
  id: DOCUMENT.FROM_TEMPLATE,
  iconName: 'kanbo-24-add-new-document-from-template',
  formattedMessageId: 'DOCUMENT_ADD_FROM_TEMPLATE',
}, {
  id: DOCUMENT.WORD,
  iconName: 'kanbo-24-create-new-word-document',
  formattedMessageId: 'DOCUMENT_WORD_ADD_NEW',
}, {
  id: DOCUMENT.EXCEL,
  iconName: 'kanbo-24-create-new-excel-document',
  formattedMessageId: 'DOCUMENT_EXCEL_ADD_NEW',
}, {
  id: DOCUMENT.POWERPOINT,
  iconName: 'kanbo-24-create-new-powerpoint-document',
  formattedMessageId: 'DOCUMENT_POWERPOINT_ADD_NEW',
}]

const ALL_ELEMENTS = [
  ...CARD_ELEMENTS,
  ...CARD_TEMPLATE_ELEMENTS,
  ...DOCUMENTS,
]

export class AddElement extends React.Component {
  static propTypes = {
    intl: PropTypes.object.isRequired,

    id: PropTypes.number.isRequired,
    boardId: PropTypes.number.isRequired,
    isTemplate: PropTypes.bool.isRequired,

    requestAddNote: PropTypes.func.isRequired,
    requestAddChecklist: PropTypes.func.isRequired,
    requestAddDocumentList: PropTypes.func.isRequired,
    requestAddDocumentFolder: PropTypes.func.isRequired,
    requestAddDocumentFolderCreator: PropTypes.func.isRequired,
    requestAddSubCardGroup: PropTypes.func.isRequired,
    requestAddDocumentToCard: PropTypes.func.isRequired,
    requestAddDocumentFromTemplate: PropTypes.func.isRequired,
  }

  state = {
    addingElement: false,
    selectedFolder: null,
  }

  close = () => {
    if (!this.state.selectedFolder) {
      this.setState({
        addingElement: false
      })
    }
  }

  handleModalRef = (ref) => {
    if (ref) ref.style.width = findDOMNode(this).offsetWidth + 'px'
  }

  handleAddElementClick = () => {
    this.setState({
      addingElement: true,
      selectedFolder: null,
    })
  }

  handleNoteValidate = ({ name }) => {
    const { intl } = this.props

    const errors = {}

    if (!name) {
      errors.name = intl.formatMessage({ id: 'CARD_ELEMENT_NAME_EMPTY' })
    } else if (name.length > CARD_ELEMENT_NAME_MAX_LENGTH) {
      errors.name = intl.formatMessage({ id: 'CARD_ELEMENT_NAME_TOO_LONG' }, { limit: CARD_ELEMENT_NAME_MAX_LENGTH })
    }

    return errors
  }

  handleNoteSubmit = ({ name, content }) => {
    const {
      intl,
      id,
      requestAddNote
    } = this.props

    return new Promise((resolve) => {
      const contentCharCount = getCharCount(content)
      if (contentCharCount === 0) {
        throw new SubmissionError({ content: intl.formatMessage({ id: 'NOTE_EMPTY' }) })
      } else if (contentCharCount > NOTE_CONTENT_MAX_LENGTH) {
        throw new SubmissionError({ content: intl.formatMessage({ id: 'NOTE_TOO_LONG' }, { limit: NOTE_CONTENT_MAX_LENGTH }) })
      }

      requestAddNote({
        cardId: id,
        name,
        content: trimRichText(content),
        index: 0
      })

      resolve()

      this.close()
    })
  }

  handleElementValidate = ({ name }) => {
    const { intl } = this.props

    const errors = {}

    if (name && name.length > CARD_ELEMENT_NAME_MAX_LENGTH) {
      errors.name = intl.formatMessage({ id: 'CARD_ELEMENT_NAME_TOO_LONG' }, { limit: CARD_ELEMENT_NAME_MAX_LENGTH })
    }

    return errors
  }

  handleChecklistSubmit = ({ name }) => {
    const { id, requestAddChecklist } = this.props

    if (name) {
      requestAddChecklist({
        cardId: id,
        name,
        index: 0
      })
      this.close()
    }
  }

  handleDocumentListSubmit = ({ name }) => {
    const { id, requestAddDocumentList } = this.props

    if (name) {
      requestAddDocumentList({
        cardId: id,
        name,
        position: 0
      })
      this.close()
    }
  }

  handleDocumentFolderSubmit = ({ name, folder }) => {
    const { id, requestAddDocumentFolder } = this.props

    if (name) {
      requestAddDocumentFolder({
        cardId: id,
        name,
        documentSourceId: folder.documentSourceId,
        path: folder.type === 'folder' ? folder.url : undefined,
        position: 0
      })

      this.setState({
        addingElement: false,
        selectedFolder: null,
      })
    }
  }

  handleDocumentFolderCreatorSubmit = ({ name, folder }) => {
    const { id, requestAddDocumentFolderCreator } = this.props

    if (name) {
      requestAddDocumentFolderCreator({
        cardId: id,
        name,
        documentSourceId: folder.documentSourceId,
        path: folder.type === 'folder' ? folder.url : undefined,
        position: 0
      })

      this.setState({
        addingElement: false,
        selectedFolder: null,
      })
    }
  }

  handleSubCardGroupSubmit = ({ name }) => {
    const { id, requestAddSubCardGroup } = this.props

    if (name) {
      requestAddSubCardGroup({
        cardId: id,
        name,
        position: 0
      })
      this.close()
    }
  }

  handleAddDocumentFromTemplateSubmit = ({ name, extension, templateSourceId, templateIdentifier }) => {
    const { id, boardId, requestAddDocumentFromTemplate } = this.props

    if (name) {
      requestAddDocumentFromTemplate({
        boardId,
        cardId: id,
        url: `${name.trim()}.${extension}`,
        templateSourceId,
        templateIdentifier,
      })

      this.close()
    }
  }

  renderDocument = (props) => {
    const { id, requestAddDocumentToCard } = this.props

    const documentProps = props.type === 'document' ? {
      menu: [{
        icon: <KanBoIcon fixedWidth name='kanbo-16-attach-to-card' />,
        title: <FormattedMessage id='DOCUMENTS_ADD_TO_CARD' />,
        onClick: () => {
          requestAddDocumentToCard({
            cardId: id,
            url: props.url,
            documentSourceId: props.documentSourceId
          })
        }
      }],
      onClick: () => {
        requestAddDocumentToCard({
          cardId: id,
          url: props.url,
          documentSourceId: props.documentSourceId,
        })

        this.close()
      }
    } : {}

    return (
      <DocumentItem
        {...props}
        {...documentProps}
      />
    )
  }

  render () {
    const {
      leftSidebar,
      intl,
      boardId,
      isTemplate,
    } = this.props
    const {
      addingElement,
      selectedFolder,
    } = this.state

    return (
      <>
        <ToggleModal
          backdrop
          show={
            addingElement &&
            addingElement !== DOCUMENT.EXISTING &&
            addingElement !== DOCUMENT.FROM_TEMPLATE &&
            !(addingElement === ELEMENT.DOCUMENT_FOLDER && selectedFolder === null) &&
            !(addingElement === ELEMENT.DOCUMENT_FOLDER_CREATOR && selectedFolder === null)
          }
          onToggle={show => {
            if (!show) {
              this.setState({
                addingElement: false,
                selectedFolder: null,
              })
            }
          }}
          attachment='top center'
          targetAttachment='top center'
        >
          <Content>
            <AddElementButton
              icon={<KanBoIcon name='kanbo-24-plus-big' />}
              onClick={this.handleAddElementClick}
            >
              <FormattedMessage id='CARD_ADD_ELEMENT' />
            </AddElementButton>
          </Content>
          <div ref={this.handleModalRef}>
            {(() => {
              if (addingElement) {
                if (addingElement === true) {
                  return (
                    <Content>
                      <ModalMenuItem header>
                        <FormattedMessage id='CARD_ELEMENTS' />
                      </ModalMenuItem>
                      {CARD_ELEMENTS.map(element =>
                        <AddElementButton
                          key={element.id}
                          icon={<KanBoIcon name={element.iconName} />}
                          onClick={() => {
                            this.setState({
                              addingElement: element.id,
                              selectedFolder: null,
                            })
                          }}
                        >
                          <FormattedMessage id={element.formattedMessageId} />
                        </AddElementButton>
                      )}
                      {isTemplate &&
                        <>
                          <ModalMenuItem header>
                            <FormattedMessage id='CARD_TEMPLATE_ELEMENTS' />
                          </ModalMenuItem>
                          {CARD_TEMPLATE_ELEMENTS.map(element =>
                            <AddElementButton
                              key={element.id}
                              icon={<KanBoIcon name={element.iconName} />}
                              onClick={() => {
                                this.setState({
                                  addingElement: element.id,
                                  selectedFolder: null,
                                })
                              }}
                            >
                              <FormattedMessage id={element.formattedMessageId} />
                            </AddElementButton>
                          )}
                        </>
                      }
                    </Content>
                  )
                } else if (addingElement === ELEMENT.DOCUMENT) {
                  return (
                    <Content>
                      {DOCUMENTS.map(element =>
                        <AddElementButton
                          key={element.id}
                          icon={<KanBoIcon name={element.iconName} />}
                          onClick={() => {
                            this.setState({
                              addingElement: element.id,
                              selectedFolder: null,
                            })
                          }}
                        >
                          <FormattedMessage id={element.formattedMessageId} />
                        </AddElementButton>
                      )}
                    </Content>
                  )
                } else {
                  const element = ALL_ELEMENTS.find(element => element.id === addingElement)

                  return (
                    <Content
                      className={classes.addElementContent}
                    >
                      {element &&
                        <ContentHeader>
                          <ElementContentIcon>
                            <KanBoIcon name={element.iconName} />
                          </ElementContentIcon>
                          <ContentTitle>
                            <FormattedMessage id={element.formattedMessageId} />
                          </ContentTitle>
                        </ContentHeader>
                      }
                      <ContentBody>
                        {addingElement === ELEMENT.NOTE &&
                          <AddNoteForm
                            boardId={boardId}
                            initialValues={{
                              name: intl.formatMessage({ id: 'NOTE_HEADER' }),
                              content: createEmptyRichText()
                            }}
                            validate={this.handleNoteValidate}
                            onSubmit={this.handleNoteSubmit}
                            onCancelClick={this.close}
                          />
                        }
                        {addingElement === ELEMENT.CHECKLIST &&
                          <AddChecklistForm
                            initialValues={{
                              name: intl.formatMessage({ id: 'CHECKLIST_HEADER' })
                            }}
                            validate={this.handleElementValidate}
                            onSubmit={this.handleChecklistSubmit}
                            onCancelClick={this.close}
                          />
                        }
                        {addingElement === ELEMENT.DOCUMENT_LIST &&
                          <AddDocumentListForm
                            initialValues={{
                              name: intl.formatMessage({ id: 'DOCUMENT_LIST' })
                            }}
                            validate={this.handleElementValidate}
                            onSubmit={this.handleDocumentListSubmit}
                            onCancelClick={this.close}
                          />
                        }
                        {addingElement === ELEMENT.DOCUMENT_FOLDER &&
                          <AddDocumentFolderForm
                            initialValues={{
                              folder: selectedFolder,
                              name: intl.formatMessage({ id: 'DOCUMENT_FOLDER' })
                            }}
                            validate={this.handleElementValidate}
                            onSubmit={this.handleDocumentFolderSubmit}
                            onCancelClick={() => {
                              this.setState({
                                addingElement: false,
                                selectedFolder: null,
                              })
                            }}
                          />
                        }
                        {addingElement === ELEMENT.DOCUMENT_FOLDER_CREATOR &&
                          <AddDocumentFolderForm
                            initialValues={{
                              folder: selectedFolder,
                              name: intl.formatMessage({ id: 'DOCUMENT_FOLDER_CREATOR' })
                            }}
                            validate={this.handleElementValidate}
                            onSubmit={this.handleDocumentFolderCreatorSubmit}
                            onCancelClick={() => {
                              this.setState({
                                addingElement: false,
                                selectedFolder: null,
                              })
                            }}
                          />
                        }
                        {addingElement === ELEMENT.SUBCARD_GROUP &&
                          <AddSubCardGroupForm
                            initialValues={{
                              name: intl.formatMessage({ id: 'SUBCARD_GROUP' })
                            }}
                            validate={this.handleElementValidate}
                            onSubmit={this.handleSubCardGroupSubmit}
                            onCancelClick={this.close}
                          />
                        }
                        {addingElement === DOCUMENT.WORD &&
                          <AddOfficeDocumentForm
                            initialValues={{
                              name: '',
                              extension: 'docx',
                              templateIdentifier: 'word-docx',
                            }}
                            onSubmit={this.handleAddDocumentFromTemplateSubmit}
                            onCancelClick={this.close}
                          />
                        }
                        {addingElement === DOCUMENT.EXCEL &&
                          <AddOfficeDocumentForm
                            initialValues={{
                              name: '',
                              extension: 'xlsx',
                              templateIdentifier: 'xlsx',
                            }}
                            onSubmit={this.handleAddDocumentFromTemplateSubmit}
                            onCancelClick={this.close}
                          />
                        }
                        {addingElement === DOCUMENT.POWERPOINT &&
                          <AddOfficeDocumentForm
                            initialValues={{
                              name: '',
                              extension: 'pptx',
                              templateIdentifier: 'pptx',
                            }}
                            onSubmit={this.handleAddDocumentFromTemplateSubmit}
                            onCancelClick={this.close}
                          />
                        }
                      </ContentBody>
                    </Content>
                  )
                }
              }
            })()}
          </div>
        </ToggleModal>
        {addingElement === DOCUMENT.EXISTING &&
          <ModalWithBoardContext
            boardId={boardId}
            onClose={this.close}
          >
            <BoardModalView
              title={<FormattedMessage id='CARD_ADD_EXISTING_DOCUMENT_SELECT_DOCUMENT' />}
            >
              <Documents
                autoSelectDocumentSource
                boardId={boardId}
                documentComponent={this.renderDocument}
              />
            </BoardModalView>
          </ModalWithBoardContext>
        }
        {addingElement === DOCUMENT.FROM_TEMPLATE &&
          <ModalWithBoardContext
            boardId={boardId}
            onClose={this.close}
          >
            <BoardModalView
              title={<FormattedMessage id='DOCUMENT_ADD_FROM_TEMPLATE' />}
            >
              <DocumentTemplates
                autoSelect
                boardId={boardId}
                documentTemplateComponent={(props) =>
                  <DocumentTemplateItem
                    {...props}
                    onClick={props.type === 'template' ? () => {
                      leftSidebar.push(() =>
                        <LeftSidebarView
                          title={<FormattedMessage id='DOCUMENT_ADD_FROM_TEMPLATE' />}
                        >
                          <FormGroup>
                            <ViewBody>
                              <ControlLabel><FormattedMessage id='DOCUMENT_ADD_FROM_TEMPLATE_SELECTED_DOCUMENT' /></ControlLabel>
                            </ViewBody>
                            <DocumentTemplateItem
                              {...props}
                            />
                          </FormGroup>
                          <ViewBody>
                            <AddOfficeDocumentForm
                              initialValues={{
                                name: '',
                                extension: props.extension,
                                templateSourceId: props.documentTemplateSourceId,
                                templateIdentifier: props.identifier
                              }}
                              onSubmit={this.handleAddDocumentFromTemplateSubmit}
                              onCancelClick={this.close}
                            />
                          </ViewBody>
                        </LeftSidebarView>
                      )
                    } : props.onClick}
                  />
                }
              />
            </BoardModalView>
          </ModalWithBoardContext>
        }
        {(
          (
            addingElement === ELEMENT.DOCUMENT_FOLDER ||
            addingElement === ELEMENT.DOCUMENT_FOLDER_CREATOR
          ) && selectedFolder === null
        ) &&
          <ModalWithBoardContext
            boardId={boardId}
            onClose={this.close}
          >
            <BoardModalView
              title={<FormattedMessage id='DOCUMENT_FOLDER_SELECT_SOURCE' />}
            >
              <Documents
                boardId={boardId}
                documentComponent={(props) =>
                  <DocumentItem
                    {...props}
                    name={
                      (
                        props.type === 'source' ||
                        props.type === 'folder'
                      ) ? (
                        <>
                          {props.name}
                          <SelectButton
                            bsSize='xsmall'
                            bsStyle='primary'
                            onClick={(event) => {
                              event.stopPropagation()

                              this.setState({
                                selectedFolder: {
                                  type: props.type,
                                  name: props.name,
                                  documentSourceId: props.documentSourceId,
                                  url: props.url,
                                },
                              })
                            }}
                          >
                            <FormattedMessage id='DOCUMENT_FOLDER_SELECT_SOURCE_SELECT' />
                          </SelectButton>
                        </>
                        ) : props.name
                    }
                  />
                }
              />
            </BoardModalView>
          </ModalWithBoardContext>
        }
      </>
    )
  }
}

export default injectIntl(withLeftSidebar(AddElement))
